import {
  FC,
  Fragment,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'

import { Button } from 'featureSliced/entities/Button'
import { RenderIf } from 'components/RenderIf'

import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'

import { getProductDiscount } from 'featureSliced/shared/utils/getProductDiscount'
import {
  getPrecision,
  roundValueTo,
} from 'featureSliced/shared/utils/roundValueTo'
import { getDiscountedPrice } from 'featureSliced/shared/utils/getDiscountedPrice'
import { getItemUnits } from 'featureSliced/shared/utils/getItemUnits'
import {
  buildFixedBucketItems,
  getChoosableStepIndexes,
  getFixedProductIds,
} from 'featureSliced/shared/utils/menuStepChoices'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'

import {
  IMenuItemsBucket,
  ProductTemplateIdType,
} from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import {
  IMenu,
  IMenuEntry,
  IProduct,
} from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { useOpenProductInfoModal } from '../../../useOpenProductInfoModal'
import { useWebLayout } from 'featureSliced/shared/hooks/useWebLayout'
import { AllergenImages } from 'featureSliced/shared/utils/AllergenImages'
import { ProductInfoModal } from 'featureSliced/widgets/Modals/ProductInfoModal'

import { ReactComponent as CameraIcon } from 'assets/cameraIcon.svg'
import chevronDown from 'assets/chevronDown.svg'
import {
  ItemsContainer,
  ItemsGrid,
  ItemsGridExpander,
  MainTitle,
  Item,
  ItemImageContainer,
  ItemImage,
  ItemPrice,
  ItemInfoIcon,
  ItemInfoIconCorner,
  ItemTitleRow,
  ItemTitleText,
  ItemAllergens,
  ItemAllergen,
  ItemBody,
  ItemDetails,
  ItemRemove,
  StepRow,
  StepNode,
  StepNodeCircle,
  StepLabel,
  StepStatus,
} from './styled'

// a sub-article has something to show when the catalogue carries allergens or a
// note, or the product-info item exists; zero ids never reach /product-info
const hasSubArticleInfo = (item: IMenuEntry) =>
  item.categoryId > 0 &&
  item.id > 0 &&
  (item.hasProductInfo === true ||
    Boolean(item.allergens?.length) ||
    Boolean(item.note))
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'

type AddMenuItemsProps = {
  product: IProduct
  productTemplateId: ProductTemplateIdType
  closeModal: () => void
  registerBackHandler?: (handler: (() => boolean) | null) => void
}

export const AddMenuItems: FC<AddMenuItemsProps> = ({
  product,
  closeModal,
  productTemplateId,
  registerBackHandler,
  ...selectedKitchenInfo
}) => {
  // Hooks
  const { getProductName, getCategoryName } = useGetTranslatedNames()
  const dispatch = useMyDispatch()
  // The parent step's kitchen-info choice arrives once via propsNext (Modal's
  // step-to-step relay, components/Modal/index.tsx). Opening a stacked modal
  // from in here (the sub-article info sheet) changes the modal count, which
  // clears propsNext — so the choice must be captured on mount, not re-read
  // from props on every render.
  const [initialKitchenInfo] = useState(() => selectedKitchenInfo)
  const t = useTranslations()
  const { getProductPrice } = useGetProductPrice()
  const openProductInfo = useOpenProductInfoModal()
  const { isWeb } = useWebLayout()

  // Actions
  const addProductToBucket = QRMBucketSlice.actions.addProductToBucket

  // Store
  const Currency = useMySelector((state) => state.app.companyData.Currency)
  const discounts = useMySelector((state) => state.specials?.discounts)

  // v2: menus come directly from the product
  const menus = useMemo(() => product.menus || [], [product])
  // A group the guest cannot decide anything in is never rendered — its fixed
  // components are added anyway and named in the product card (QRM-665).
  const fixedProductIds = useMemo(() => getFixedProductIds(menus), [menus])
  const steps = useMemo(() => getChoosableStepIndexes(menus), [menus])

  // State
  const [stepPos, setStepPos] = useState(0)
  const [selectedMenuItems, setSelectedMenuItems] = useState<
    IMenuItemsBucket[]
  >([])
  const [isItemsCollapsed, setIsItemsCollapsed] = useState<boolean>(false)
  const [isCollapsedTouched, setIsCollapsedTouched] = useState<boolean>(false)
  const [expandedDetailsId, setExpandedDetailsId] = useState<number | null>(
    null
  )
  const hasOrderedRef = useRef(false)
  // the back handler handed to the modal is registered once and must not go
  // stale — it reads the position through this ref
  const stepPosRef = useRef(0)
  // step positions the guest has moved past — an optional step counts as done
  // once it was passed, not only once something was picked in it
  const [passedSteps, setPassedSteps] = useState<Set<number>>(() => new Set())
  // once a node was revisited the last step commits only via the button: a
  // pick swapped on a full last step must not book the line on the spot
  const revisitedRef = useRef(false)
  // the auto-advance below reacts to additions only
  const lastChangeRef = useRef<'add' | 'remove' | null>(null)
  const fixedBucketItems = useMemo(
    () => buildFixedBucketItems(menus, discounts, Currency),
    [menus, discounts, Currency]
  )

  stepPosRef.current = stepPos
  const currentSelected = steps[stepPos] ?? 0
  const isLastStep = stepPos + 1 >= steps.length

  // the step label is the referenced template's name, translated like any
  // category (what the Smart_Checkout shows); the raw Sets text is the fallback
  // for cache items written before the template travelled with the step
  const stepLabel = (menu?: IMenu) => {
    if (!menu) return ''
    if (menu.templateId) {
      return getCategoryName({
        id: menu.templateId,
        name: menu.templateName || menu.name,
      })
    }
    return menu.name
  }

  const chosenCount = (menuIndex: number) =>
    selectedMenuItems.filter((el) => el.menuIndex === menuIndex && !el.isFixed)
      .length

  const isStepDone = (pos: number) => {
    const menuIndex = steps[pos]
    const min = Number(menus[menuIndex]?.min) || 0
    const chosen = chosenCount(menuIndex)
    if (chosen < min) return false
    return chosen > 0 || passedSteps.has(pos)
  }

  const goToStep = (pos: number) => {
    const from = stepPosRef.current
    if (pos === from || pos < 0 || pos >= steps.length) return
    if (pos < from) revisitedRef.current = true
    setPassedSteps((prev) => {
      const next = new Set(prev)
      for (let i = 0; i < pos; i++) next.add(i)
      return next
    })
    setStepPos(pos)
  }

  useEffect(() => {
    if (!registerBackHandler) return
    registerBackHandler(() => {
      if (stepPosRef.current <= 0) return false
      goToStep(stepPosRef.current - 1)
      return true
    })
    return () => registerBackHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerBackHandler, steps.length])

  // useEffects
  useEffect(() => {
    if (!isCollapsedTouched && menus[currentSelected]?.items?.length > 6) {
      setIsItemsCollapsed(true)
    }
  }, [menus, currentSelected])

  // Every group's fixed components land at once: a group that is never rendered
  // still contributes its own (QRM-665). Same shape the single-step modal builds.
  useEffect(() => {
    if (!fixedBucketItems.length) return
    setSelectedMenuItems((prev) => {
      const existing = new Set(
        prev.map((el) => `${el.menuIndex}_${el.productId}`)
      )
      const toAdd = fixedBucketItems.filter(
        (el) => !existing.has(`${el.menuIndex}_${el.productId}`)
      )
      return toAdd.length ? [...prev, ...toAdd] : prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedBucketItems])

  useEffect(() => {
    // max 0 = no limit (Pavel, QRM-665): such a group never fills up, and the
    // old `0 >= 0` test skipped it before the guest saw it — twice, because the
    // sheet stays mounted for the close animation.
    const max = Number(menus?.[currentSelected]?.max) || 0
    if (max <= 0) return
    // only an addition can fill a step; a removal never advances or books
    if (lastChangeRef.current !== 'add') return
    const chosen = selectedMenuItems.filter(
      (el) => el.menuIndex === currentSelected
    ).length
    if (chosen < max) return
    if (isLastStep) {
      if (revisitedRef.current) return
      orderProduct()
      return
    }
    // the next step is already complete (the guest came back to change a
    // pick) — stay, the Smart_Checkout does the same
    if (isStepDone(stepPos + 1)) return
    orderOrNext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMenuItems])

  useEffect(() => {
    // defensive: nothing to render at all. Never before the fixed components are
    // in state, or the line would go out without them.
    if (steps.length) return
    if (selectedMenuItems.length < fixedBucketItems.length) return
    orderProduct()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length, selectedMenuItems, fixedBucketItems])

  // Functions
  const addItemToSelected = (item: any, menuIndex: number) => {
    // an article the template already attaches cannot be taken a second time —
    // that is how one deposit became two (QRM-665)
    if (item.isFixed || fixedProductIds.has(item.id)) return
    const discountP = getProductDiscount(item, discounts)
    const finalPrice = item.price ?? 0
    lastChangeRef.current = 'add'
    setSelectedMenuItems((prevValue) => [
      ...prevValue,
      {
        productId: item.id,
        count: 1,
        menuIndex,
        menuId: menus[menuIndex]?.id,
        price: finalPrice,
        name: item.title,
        kitchenInfo: item.kitchenInfo ?? [],
        imageUrl: item.imageUrl ?? undefined,
        categoryId: item.categoryId,
        unitSellCode: item.unitSellCode,
        isApplyDiscount: Boolean(discountP),
        isFixed: false,
        total: roundValueTo(
          getDiscountedPrice(finalPrice, discountP),
          getPrecision(Currency)
        ),
      } as unknown as IMenuItemsBucket,
    ])
  }

  // takes one unit of the item out of the current step; a fixed component is
  // never removable (the template attaches it), and the step goes back to
  // "open" when it drops below its minimum
  const removeItemFromSelected = (productId: number, menuIndex: number) => {
    lastChangeRef.current = 'remove'
    setSelectedMenuItems((prevValue) => {
      const at = prevValue.findIndex(
        (el) =>
          el.productId === productId &&
          el.menuIndex === menuIndex &&
          !el.isFixed
      )
      if (at < 0) return prevValue
      return [...prevValue.slice(0, at), ...prevValue.slice(at + 1)]
    })
  }

  const orderOrNext = () => {
    if (!isLastStep) {
      goToStep(stepPos + 1)
      return
    }
    orderProduct()
  }

  const orderProduct = () => {
    // the sheet stays mounted for 280ms after closeModal(), so an effect can
    // still fire and dispatch the same line twice
    if (hasOrderedRef.current) return
    hasOrderedRef.current = true
    const { id, categoryId, unitSellCode } = product
    const sKI = JSON.stringify(initialKitchenInfo)
    // the step is part of the line's identity: the same components reached
    // through different steps are different compositions
    const sMI = selectedMenuItems
      ?.map((sMI) => `${sMI.menuId ?? sMI.menuIndex}:${sMI.productId}`)
      ?.sort()
    const productToAdd = {
      uniqueId: `${productTemplateId}_${id}_${sKI}_${sMI}`,
      productId: id,
      productTemplateId,
      categoryId,
      count: 0, // 0 is needed for UPDATE to work correctly
      kitchenInfo: JSON.stringify(initialKitchenInfo || {}),
      price: getProductPrice(product, productTemplateId).finalPrice,
      addedAsWeight: unitSellCode === 2 || unitSellCode === 3,
      menuItems: selectedMenuItems,
    }
    dispatch(addProductToBucket(productToAdd))
    closeModal()
  }

  const getSlicedArray = <T,>(arrayToSlice: T[]): T[] => {
    if (!isCollapsedTouched && isItemsCollapsed) {
      return arrayToSlice.slice(0, 6)
    }
    return arrayToSlice
  }

  const currentMenu = menus[currentSelected]
  const currentChosen = chosenCount(currentSelected)
  const minCount = currentMenu?.min || 0
  // for the heading only: an unlimited group has no number to promise, so the
  // item count stands in for it
  const maxCount = Number(currentMenu?.max) || currentMenu?.items?.length || 1

  return (
    <div style={{ marginTop: '35px' }}>
      <RenderIf condition={steps.length > 1}>
        <StepRow
          role="tablist"
          aria-label={t('menuSteps.stepOf', {
            step: stepPos + 1,
            total: steps.length,
          })}
        >
          {steps.map((menuIndex, pos) => {
            const isCurrent = pos === stepPos
            const isDone = !isCurrent && isStepDone(pos)
            return (
              <StepNode
                key={`menu-step_${menuIndex}`}
                type="button"
                role="tab"
                aria-selected={isCurrent}
                data-testid={`menu_step_${pos + 1}`}
                data-state={isCurrent ? 'current' : isDone ? 'done' : 'open'}
                onClick={() => goToStep(pos)}
              >
                <StepNodeCircle isCurrent={isCurrent} isDone={isDone}>
                  {isDone ? '✓' : pos + 1}
                </StepNodeCircle>
                <StepLabel isCurrent={isCurrent}>
                  {stepLabel(menus[menuIndex])}
                </StepLabel>
              </StepNode>
            )
          })}
        </StepRow>
      </RenderIf>
      <MainTitle>
        {minCount === maxCount
          ? t('products.buildPerfectSpecific', {
              productName: getProductName(product),
              count: minCount || 1,
            })
          : t('products.buildPerfect', {
              productName: getProductName(product),
              minCount,
              maxCount,
            })}
      </MainTitle>
      <StepStatus data-testid="menu_step_status">
        <span>{t('menuSteps.selected', { value: currentChosen })}</span>
        <span>{t('menuSteps.min', { value: minCount })}</span>
        <span>
          {t('menuSteps.max', { value: Number(currentMenu?.max) || '–' })}
        </span>
      </StepStatus>
      <ItemsContainer>
        <ItemsGrid>
          {getSlicedArray(currentMenu?.items || []).map((item) => {
            const isFixed = Boolean(item.isFixed)
            return (
              <Fragment key={`menu-item_${item.id}`}>
                <Item
                  onClick={() => addItemToSelected(item, currentSelected)}
                  isSelected={
                    isFixed ||
                    Boolean(
                      selectedMenuItems?.find(
                        (selectedItem) => selectedItem.productId === item.id
                      )
                    )
                  }
                  count={
                    isFixed
                      ? item.count ?? 1
                      : getItemUnits(item.id, selectedMenuItems)
                  }
                  style={
                    isFixed ? { cursor: 'default', opacity: 0.85 } : undefined
                  }
                >
                  <RenderIf
                    condition={
                      !isFixed &&
                      getItemUnits(item.id, selectedMenuItems) !== undefined &&
                      chosenCount(currentSelected) > 0 &&
                      selectedMenuItems.some(
                        (el) =>
                          el.productId === item.id &&
                          el.menuIndex === currentSelected &&
                          !el.isFixed
                      )
                    }
                  >
                    <ItemRemove
                      type="button"
                      aria-label={t('common.delete')}
                      data-testid={`menu_item_remove_${item.id}`}
                      onClick={(e: MouseEvent) => {
                        e.stopPropagation()
                        removeItemFromSelected(item.id, currentSelected)
                      }}
                    >
                      −
                    </ItemRemove>
                  </RenderIf>
                  <RenderIf condition={!isWeb && hasSubArticleInfo(item)}>
                    <ItemInfoIconCorner
                      type="button"
                      aria-label={t('productInfo.open')}
                      data-testid={`menu_item_info_${item.id}`}
                      onClick={(e: MouseEvent) => {
                        e.stopPropagation()
                        openProductInfo(item, { withCatalogueSections: true })
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <path d="M12 11v6" />
                        <path d="M12 7.5h.01" />
                      </svg>
                    </ItemInfoIconCorner>
                  </RenderIf>
                  <ItemImageContainer>
                    <RenderIf condition={!!item.imageUrl}>
                      <ItemImage src={item.imageUrl} />
                    </RenderIf>
                    <RenderIf condition={!item.imageUrl}>
                      <CameraIcon />
                    </RenderIf>
                  </ItemImageContainer>
                  <ItemBody>
                    <ItemTitleRow>
                      <ItemTitleText>{getProductName(item)}</ItemTitleText>
                      <RenderIf condition={isWeb && hasSubArticleInfo(item)}>
                        <ItemInfoIcon
                          type="button"
                          aria-label={t('productInfo.open')}
                          data-testid={`menu_item_details_${item.id}`}
                          aria-expanded={expandedDetailsId === item.id}
                          onClick={(e: MouseEvent) => {
                            e.stopPropagation()
                            setExpandedDetailsId((prev) =>
                              prev === item.id ? null : item.id
                            )
                          }}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            aria-hidden="true"
                          >
                            <path d="M12 11v6" />
                            <path d="M12 7.5h.01" />
                          </svg>
                        </ItemInfoIcon>
                      </RenderIf>
                    </ItemTitleRow>
                    <RenderIf condition={!!item.allergens?.length}>
                      <ItemAllergens
                        data-testid={`menu_item_allergens_${item.id}`}
                      >
                        {item.allergens?.map((allergen) => (
                          <ItemAllergen
                            key={`menu-item-allergen_${item.id}_${allergen.id}`}
                            alt={String(allergen.id)}
                            src={
                              AllergenImages[allergen.id] || AllergenImages[0]
                            }
                          />
                        ))}
                      </ItemAllergens>
                    </RenderIf>
                  </ItemBody>
                  <ItemPrice>
                    {item.price
                      ? `${convertToPrice(item.price)} ${Currency || ''}`
                      : ''}
                  </ItemPrice>
                </Item>
                {/* a SIBLING of Item, not a child — kept out of the reach
                  of Item's own `p` rule (dark grey, forced size, meant for
                  the tile's title/price), which would otherwise leak into
                  the info body this hosts */}
                <RenderIf condition={isWeb && expandedDetailsId === item.id}>
                  <ItemDetails>
                    <ProductInfoModal
                      categoryId={item.categoryId}
                      productId={item.id}
                      fetchDetails={item.hasProductInfo === true}
                      withCatalogueSections
                      allergens={item.allergens}
                      allergenLabels={item.allergenLabels}
                      note={item.note}
                    />
                  </ItemDetails>
                </RenderIf>
              </Fragment>
            )
          })}
        </ItemsGrid>
        <RenderIf condition={isItemsCollapsed}>
          <ItemsGridExpander
            onClick={() => {
              setIsItemsCollapsed(false)
              setIsCollapsedTouched(true)
            }}
          >
            <img alt="Show all" src={chevronDown} />
          </ItemsGridExpander>
        </RenderIf>
      </ItemsContainer>
      <Button
        width="100%"
        buttonType="common"
        onClick={orderOrNext}
        disabled={
          selectedMenuItems.filter((el) => el.menuIndex === currentSelected)
            ?.length < minCount
        }
      >
        {isLastStep ? t('buttons.accept') : t('common.next')}
      </Button>
    </div>
  )
}
